/**
 * Subscriptions API Client
 *
 * HTTP REST API endpoints for Subscriptions functionality:
 * - Get subscription info and usage
 * - Create, upgrade, downgrade subscriptions
 * - Manage subscription lifecycle
 *
 * Uses HTTP-only cookies for authentication (via fetchWithTokenRefresh)
 */

import { buildApiUrl } from './config';
import { fetchWithTokenRefresh } from './refreshToken';
import { handleApiError } from './errorHandler';

export interface SubscriptionUsage {
  tier: 'FREE' | 'STANDARD' | 'PROFESSIONAL' | 'ADVANCED';
  limits: {
    maxCategories: number;
    maxPortfolioItems: number;
    maxProposals: number;
    canUsePriorityListing: boolean;
    canUseAnalytics: boolean;
    canUseFeaturedBadge: boolean;
  };
  usage: {
    categories: number;
    portfolioItems: number;
    activeProposals: number;
  };
  subscription: {
    id: string | null;
    status: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  };
}

export interface Subscription {
  id: string;
  contractorId: string;
  tier: string;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  stripeSubscriptionId: string | null;
}

export const subscriptionsApi = {
  /**
   * Get current user's subscription
   * GET /subscriptions/me
   */
  async getMySubscription(): Promise<Subscription> {
    const url = buildApiUrl('/subscriptions/me');
    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to fetch subscription');
    }

    return response.json();
  },

  /**
   * Get subscription usage and limits
   * GET /subscriptions/me/usage
   */
  async getMySubscriptionUsage(): Promise<SubscriptionUsage> {
    const url = buildApiUrl('/subscriptions/me/usage');
    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to fetch subscription usage');
    }

    return response.json();
  },

  /**
   * Create a new subscription
   * POST /subscriptions
   */
  async createSubscription(tier: string, paymentMethodId?: string) {
    const url = buildApiUrl('/subscriptions');
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tier, paymentMethodId }),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to create subscription');
    }

    return response.json();
  },

  /**
   * Upgrade subscription
   * PATCH /subscriptions/upgrade
   */
  async upgradeSubscription(tier: string) {
    const url = buildApiUrl('/subscriptions/upgrade');
    const response = await fetchWithTokenRefresh(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tier }),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to upgrade subscription');
    }

    return response.json();
  },

  /**
   * Downgrade subscription
   * PATCH /subscriptions/downgrade
   */
  async downgradeSubscription(tier: string, immediate: boolean = false) {
    const url = buildApiUrl('/subscriptions/downgrade');
    const response = await fetchWithTokenRefresh(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tier, immediate }),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to downgrade subscription');
    }

    return response.json();
  },

  /**
   * Cancel subscription
   * DELETE /subscriptions
   */
  async cancelSubscription(immediate: boolean = false) {
    const url = buildApiUrl('/subscriptions');
    const response = await fetchWithTokenRefresh(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ immediate }),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to cancel subscription');
    }

    return response.json();
  },

  /**
   * Reactivate canceled subscription
   * POST /subscriptions/reactivate
   */
  async reactivateSubscription() {
    const url = buildApiUrl('/subscriptions/reactivate');
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to reactivate subscription');
    }

    return response.json();
  },

  /**
   * Get Stripe customer portal URL
   * POST /subscriptions/portal
   */
  async getCustomerPortalUrl(returnUrl: string): Promise<{ url: string }> {
    const url = buildApiUrl('/subscriptions/portal');
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ returnUrl }),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to get customer portal URL');
    }

    return response.json();
  },

  /**
   * Create Stripe Checkout session
   * POST /subscriptions/checkout
   */
  async createCheckoutSession(
    tier: string,
    successUrl?: string,
    cancelUrl?: string,
  ): Promise<{ sessionId: string; url: string }> {
    const url = buildApiUrl('/subscriptions/checkout');
    const response = await fetchWithTokenRefresh(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tier, successUrl, cancelUrl }),
    });

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to create checkout session');
    }

    return response.json();
  },

  /**
   * Get Stripe Checkout session details
   * GET /subscriptions/checkout/:sessionId
   */
  async getCheckoutSession(sessionId: string) {
    const url = buildApiUrl(`/subscriptions/checkout/${sessionId}`);
    const response = await fetchWithTokenRefresh(url);

    if (!response.ok) {
      throw await handleApiError(response, 'Failed to get checkout session');
    }

    return response.json();
  },
};
